import Header from "../components/Header";
import { AnimatePresence, motion } from "motion/react";
import {
  guideVariants,
  opacityVariants,
  routeVariants,
} from "../routesVariants";
import { Select, Spinner, Title } from "../uikit";
import Card from "../components/Card";
import { useEffect, useState } from "react";

import SupportButton from "../components/SupportButton";

import { useTranslation } from "react-i18next";
import { data, useLocation, useNavigate, useParams } from "react-router";

import CheckmarkIcon from "../img/checkmark.svg";

import GuidesData from "../guides_data";

import styles from "./guidesall.module.css";
import GuideHostnameData from "../components/guides/GuideHostnameData";
import GuideLinkData from "../components/guides/GuideLinkData";
import GuideCredsData from "../components/guides/GuideCredsData";
import { DOMAIN } from "../consts";
import GuideOvpnData from "../components/guides/GuideOvpnData";
import GuideShadowsocksData from "../components/guides/GuideShadowsocksData";
import GuideVlessLinkData from "../components/guides/GuideVlessLinkData";
import GuideVlessQrData from "../components/guides/GuideVlessQrData";
import { getUserData } from "../utils/Api";
import { getToken } from "../utils/tokenStorage";

export default function GuidesAll() {
  const navigate = useNavigate();
  const { t, _ } = useTranslation();
  const params = useParams();
  const location = useLocation();

  const [isLoaded, setIsLoaded] = useState(false);

  const [subscription, setSubscription] = useState(
    location.state?.subscription ?? {}
  );
  const [lang, setLang] = useState("en");
  const [os, setOs] = useState("android");
  const [protocol, setProtocol] = useState("");
  const [guidesByOs, setGuidesByOs] = useState([]);
  const [guideSteps, setGuideSteps] = useState([]);

  const [servers, setServers] = useState([]);
  const [shadowsocksServers, setShadowsocksServers] = useState([]);
  const [selectedHostname, setSelectedHostname] = useState("");

  const getDefaultProtocolPath = (guides = []) => {
    if (!guides.length) {
      return "";
    }
    const openVpnGuide = guides.find(
      (guide) =>
        guide.path?.toLowerCase() === "openvpn" ||
        guide.protocol?.toLowerCase() === "openvpn"
    );
    return (openVpnGuide || guides[0]).path;
  };

  const getServers = async () => {
    const token = getToken();
    if (token == "" || token == null) {
      navigate("/login");
      return;
    }
    const response = await fetch(
      DOMAIN + "/subscriptions/" + subscription.id + "/servers?purpose=input",
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    ).then((r) => r.json());
    if (response.success) {
      let data = (response.servers || []).map((row) => ({
        ...row,
        hostname: row.domain || row.name,
        request_name: row.name,
        location: row.city,
      }));
      setServers(data);
    } else {
      navigate("/account");
    }
  };

  const getShadowsocksServers = async () => {
    // Temporary stub: shadowsocks servers endpoint is disabled for now.
    setShadowsocksServers([]);
  };

  const setOsHandler = (event) => {
    const value = event.target.value;
    changeOs(value);
  };

  const changeOs = (value) => {
    const filterOs = GuidesData.GuidesInfo.filter((x) => x.path == value);
    if (!filterOs.length) {
      return false;
    }
    const nextGuides = filterOs[0]["guides"] || [];
    const defaultProtocolPath = getDefaultProtocolPath(nextGuides);
    setProtocol("");
    setOs(value);
    setGuidesByOs(nextGuides);
    setGuideSteps([]);
    if (defaultProtocolPath) {
      changeProtocol(defaultProtocolPath, value);
    }
    return true;
  };

  const changeProtocol = (value, forceOs = null) => {
    const localOs = forceOs ? forceOs : os;
    if (localOs in GuidesData && value in GuidesData[localOs]) {
      setGuideSteps(GuidesData[localOs][value]);
      setProtocol(value);
      return true;
    }
    return false;
  };

  const initGuides = () => {
    const initialOs = GuidesData.GuidesInfo[0].path;
    const initialGuides = GuidesData.GuidesInfo[0]["guides"] || [];
    const defaultProtocolPath = getDefaultProtocolPath(initialGuides);
    setOs(initialOs);
    setGuidesByOs(initialGuides);
    setGuideSteps([]);
    setProtocol("");
    if (defaultProtocolPath) {
      changeProtocol(defaultProtocolPath, initialOs);
    }
  };

  const gatherData = async () => {
    let currentSubscription = subscription;
    if (currentSubscription == null || !Object.keys(currentSubscription).length) {
      if (!("login" in params)) {
        return false;
      }

      const [success, result] = await getUserData();
      if (!success) {
        return false;
      }

      // В параметре маршрута приходит логин или subscription_id (deep link из бота)
      const subs = result.subs.filter(
        (x) =>
          x.login === params.login ||
          String(x.id) === String(params.login) ||
          String(x.subscription_id) === String(params.login)
      );
      if (subs.length == 0) {
        return false;
      }

      currentSubscription = subs[0];
      setSubscription(subs[0]);
    }

    if (!("os" in params)) {
      initGuides();
      return true;
    }
    if (!changeOs(params.os)) {
      initGuides();
      return true;
    }
    if (!("protocol" in params)) {
      return true;
    }
    changeProtocol(params.protocol, params.os);
    return true;
  };

  const prepareData = async () => {
    const status = await gatherData();
    if (!status) {
      navigate("/account");
      return;
    }
    setIsLoaded(true);
  };

  useEffect(() => {
    prepareData();
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    setLang(localStorage.getItem("lang") ?? "en");
    getServers();
    getShadowsocksServers();
  }, [isLoaded]);

  return (
    <motion.div
      className="container background-regular border-padding column-spacing column"
      variants={routeVariants}
      initial="initial"
      animate="final"
      exit="exit"
    >
      <Header
        isBack={true}
        backCallback={() => navigate("/account/guide", location)}
      >
        <Title level="1" weight="3">
          {t("Guides")}
        </Title>
      </Header>
      {isLoaded ? (
        <>
          <Card>
            <div className="column card">
              <div>
                <Select header={t("Operation system")} onChange={setOsHandler}>
                  {GuidesData.GuidesInfo.map((row) => {
                    return (
                      <option
                        value={row.path}
                        selected={os === row.path}
                        key={"select" + row.path}
                      >
                        {row.os}
                      </option>
                    );
                  })}
                </Select>
              </div>
              <AnimatePresence exitBeforeEnter>
                <motion.div
                  key={"guide" + os}
                  variants={opacityVariants}
                  initial="initial"
                  animate="final"
                  exit="exit"
                >
                  {guidesByOs && guidesByOs.length ? (
                    guidesByOs.map((row) => (
                      <div
                        className={`row pointer ${styles.protocol}`}
                        key={"guide" + os + row.path}
                        onClick={() => changeProtocol(row.path)}
                      >
                        <AnimatePresence exitBeforeEnter>
                          <motion.div
                            key={"guide" + os + protocol}
                            variants={guideVariants}
                            initial="initial"
                            animate="final"
                            exit="exit"
                            className="vertical-center"
                          >
                            {protocol == row.path && (
                              <img
                                src={CheckmarkIcon}
                                alt=""
                                className="mr-025"
                              />
                            )}
                          </motion.div>
                        </AnimatePresence>
                        <div>{row.protocol}</div>
                      </div>
                    ))
                  ) : (
                    <div></div>
                  )}
                </motion.div>
              </AnimatePresence>
              <div className="mb-1"></div>
            </div>
          </Card>
          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={"guide" + os + protocol}
              variants={opacityVariants}
              initial="initial"
              animate="final"
              exit="exit"
            >
              {guideSteps &&
                "guide" in guideSteps &&
                guideSteps.guide[lang].map((row, index) => {
                  return (
                    <Card>
                      <div className={`row ${styles.number}`}>
                        <span>{index + 1}</span>
                      </div>
                      <div className="row">
                        <div
                          dangerouslySetInnerHTML={{ __html: row.text }}
                          className={styles["guide-text"]}
                        ></div>
                      </div>
                      {"data" in row &&
                        row.data.map((dataRow) => {
                          if (!("type" in dataRow)) {
                            return;
                          }
                          switch (dataRow.type) {
                            case "link":
                              return (
                                <GuideLinkData
                                  link={dataRow.link}
                                  text={dataRow.text}
                                  label={dataRow.title ?? ""}
                                />
                              );
                            case "login":
                              return (
                                <GuideCredsData
                                  label={dataRow.title ?? t("Login2")}
                                  text={subscription.login}
                                />
                              );
                            case "password":
                              return (
                                <GuideCredsData
                                  label={dataRow.title ?? t("Password")}
                                  text={subscription.password}
                                />
                              );
                            case "psk":
                              return (
                                <GuideCredsData
                                  label={dataRow.title ?? t("Pre-shared key")}
                                  text={"a-secure-psk"}
                                />
                              );
                            case "openvpn":
                              return (
                                <GuideOvpnData
                                  subscription={subscription}
                                  servers={servers}
                                  label={dataRow.title ?? ""}
                                />
                              );
                            case "nas":
                              return (
                                <GuideHostnameData
                                  subscription={subscription}
                                  servers={servers}
                                  label={dataRow.title ?? ""}
                                  selectedHostname={selectedHostname}
                                  setSelectedHostname={(nas) =>
                                    setSelectedHostname(nas)
                                  }
                                />
                              );
                            case "shadowsocks_qr":
                              return (
                                <GuideShadowsocksData
                                  subscription={subscription}
                                  servers={shadowsocksServers}
                                  type="qr"
                                  label={dataRow.title ?? ""}
                                />
                              );
                            case "shadowsocks_link":
                              return (
                                <GuideShadowsocksData
                                  subscription={subscription}
                                  servers={shadowsocksServers}
                                  type="link"
                                  label={dataRow.title ?? ""}
                                />
                              );
                            case "vless_link":
                              return (
                                <GuideVlessLinkData
                                  subscription={subscription}
                                  label={dataRow.title ?? t("Subscription link")}
                                />
                              );
                            case "vless_qr":
                              return (
                                <GuideVlessQrData
                                  subscription={subscription}
                                  label={dataRow.title ?? t("Subscription QR code")}
                                />
                              );
                            case "parameter":
                              return (
                                <GuideCredsData
                                  label={dataRow.title ?? ""}
                                  text={dataRow.value}
                                />
                              );
                          }
                        })}
                      {row.img != "" ? (
                        <div className={`row ${styles.imgwrapper} center pb-1`}>
                          <div className={styles.overlay}></div>
                          <img
                            class={`${styles.desktop} ${styles.img}`}
                            src={`/img/guide/${os}/${protocol}/${lang}/desktop/${row.image}`}
                          />
                          <img
                            class={`${styles.mobile} ${styles.img}`}
                            src={`/img/guide/${os}/${protocol}/${lang}/mobile/${row.image}`}
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                    </Card>
                  );
                })}
            </motion.div>
          </AnimatePresence>
          <SupportButton />
        </>
      ) : (
        <div className="center full-height">
          <Spinner />
        </div>
      )}
    </motion.div>
  );
}
